import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@design-system';
import { MonitorRequestRow } from '@/features/illustration-monitor/types';

interface RequestsTableProps {
  rows: MonitorRequestRow[];
  onRowClick: (row: MonitorRequestRow) => void;
}

const statusClasses: Record<string, string> = {
  completed: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  processing: 'bg-blue-100 text-blue-800',
  queued: 'bg-yellow-100 text-yellow-800',
  initiated: 'bg-gray-100 text-gray-800',
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(value: string | null) {
  if (value === null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function clientDisplayName(row: MonitorRequestRow) {
  if (!row.client) return '(client not found)';
  const first = row.client.clientFirstName?.trim();
  const last = row.client.clientLastName?.trim();
  if (first && last) return `${first} ${last}`;
  if (last) return last;
  return row.client.clientId;
}

function advisorDisplayName(row: MonitorRequestRow) {
  if (!row.advisor) return null;
  const first = row.advisor.advisorFirstName?.trim();
  const last = row.advisor.advisorLastName?.trim();
  if (first && last) return `${first} ${last}`;
  if (last) return last;
  return row.advisor.advisorEmail;
}

export function RequestsTable({ rows, onRowClick }: RequestsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Created</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Client</TableHead>
          <TableHead>Advisor</TableHead>
          <TableHead>Quote</TableHead>
          <TableHead>PDF</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(row => (
          <TableRow
            key={row.requestId}
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => onRowClick(row)}
          >
            <TableCell className="whitespace-nowrap text-xs text-gray-600">
              {formatDate(row.createdDateTime)}
            </TableCell>
            <TableCell>
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                  statusClasses[row.status] ?? 'bg-gray-100 text-gray-800'
                }`}
              >
                {row.status}
              </span>
            </TableCell>
            <TableCell>
              <div className="font-medium text-darkPurple">{clientDisplayName(row)}</div>
              <div className="font-mono text-xs text-gray-500">{row.clientId}</div>
            </TableCell>
            <TableCell>
              {row.advisor ? (
                <>
                  <div className="font-medium text-gray-900">{advisorDisplayName(row)}</div>
                  <div className="text-xs text-gray-500">{row.advisor.advisorEmail}</div>
                </>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </TableCell>
            <TableCell>
              {row.quote ? (
                <>
                  <div className="font-medium text-gray-900">
                    {row.quote.carrierName} · {row.quote.productName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {row.quote.state} ·{' '}
                    {formatCurrency(row.quote.premiumAmount) ?? row.quote.premiumAmount}
                    {row.quote.quoteType === 'traditionalLTC' && ' (LTC)'}
                  </div>
                </>
              ) : (
                <div className="font-mono text-xs text-gray-500">{row.insuranceQuoteId}</div>
              )}
            </TableCell>
            <TableCell>
              {row.pdfUrl ? (
                <a
                  href={row.pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="text-sm text-primary underline"
                >
                  Open
                </a>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
